import find from 'lodash/find';
import { v4 as uuidv4 } from 'uuid';
import qs from 'qs';
import includes from 'lodash/includes';
import omit from 'lodash/omit';
import { fetch } from './api/fetch';
import {
  CertificateFileExtension,
  DocumentFileExtensions,
  EXHIBITOR_DIRECTORY_RECOMMENDATION,
  GalleryCollapsedViewMaxItems,
  ImageFileExtensions,
  ItemType,
  Modes,
  Packages,
  ReactSettings,
  RegRedirectConstants,
  Tenants,
  VideoFileExtensions,
} from './Constants';
import authorizationStore from '../common/api/authorizationStore';
import SurveyConfiguration from './config/SurveyConfiguration.json';
import DiscoverConfiguration from './config/DiscoverConfiguration.json';
import StringFormatter from './StringFormatter';
import cdnConfig from './cdnConfig';
import ConsentStorage from './ConsentStorage';

const filterFieldErrors = (fieldErrors, fieldNameRegex) => {
  const errors = [];
  Object.keys(fieldErrors).forEach((key) => {
    if (
      fieldNameRegex !== undefined &&
      Object.prototype.hasOwnProperty.call(fieldErrors, key) &&
      key.match(fieldNameRegex)
    ) {
      fieldErrors[key].forEach((errorMessage) => {
        if (!errors.find((x) => x === errorMessage)) {
          errors.push(errorMessage);
        }
      });
    }
  });
  return errors;
};

const constructErrorFieldMessageMapFromGraphQlResponse = (
  graphqlResponse,
  errorTranslations,
  ignorableError,
) =>
  graphqlResponse.errors.reduce((acc, cur) => {
    if (cur.extensions.code === ignorableError) return acc;
    const result = Object.assign({}, acc);
    const fieldName = cur.extensions.code.split('_')[0].split('.')[0];
    const fieldErrorTypeTokens = cur.extensions.code.split('_');
    fieldErrorTypeTokens.shift();
    if (!Object.prototype.hasOwnProperty.call(result, fieldName)) {
      result[fieldName] = [];
    }
    result[fieldName].push(
      errorTranslations[`${fieldName}_${fieldErrorTypeTokens.join('_')}`],
    );
    return result;
  }, {});

const captureResponseCodeAsError = (
  graphqlResponse,
  errorTranslations,
  errorToBeCaptured,
) => {
  const errorCodesReceived = graphqlResponse.errors.map(
    (err) => err.extensions.code,
  );
  return includes(errorCodesReceived, errorToBeCaptured)
    ? [errorTranslations[errorToBeCaptured]]
    : [];
};

const isRxAdmin = (mode) => mode === Modes.private;
const isParticipantBase = (mode, tenants) =>
  tenants === Tenants.participants || mode === Modes.mixed;

const getNavigationBaseUrl = () => {
  const location = global.window?.location;

  if (!location) {
    return 'https://localhost';
  }

  if (location.origin) {
    return location.origin;
  }

  if (location.href) {
    try {
      return new URL(location.href).origin;
    } catch (error) {
      // Fall back to hostname-based reconstruction when href is unset or invalid.
    }
  }

  if (location.hostname) {
    return `${location.protocol || 'https:'}//${location.hostname}`;
  }

  return 'https://localhost';
};

const getSafeNavigationUrl = (url) => {
  const normalizedUrl = `${url ?? ''}`.trim();

  if (!normalizedUrl || normalizedUrl.startsWith('//')) {
    throw new Error('Invalid redirect URL');
  }

  const parsedUrl = new URL(normalizedUrl, getNavigationBaseUrl());

  if (!['http:', 'https:'].includes(parsedUrl.protocol)) {
    throw new Error('Invalid redirect URL');
  }

  return parsedUrl.toString();
};

const clickLink = (linkElement) => {
  document.body.appendChild(linkElement);
  linkElement.click();
  document.body.removeChild(linkElement);
};

const setLocationHref = (url) => {
  const navigationLink = document.createElement('a');

  navigationLink.href = getSafeNavigationUrl(url);
  navigationLink.rel = 'noopener noreferrer';
  navigationLink.style.display = 'none';

  clickLink(navigationLink);
};

const getReactSettings = () =>
  global.window[ReactSettings.artistsDirectory] ||
  global.window[ReactSettings.artistsDetails] ||
  global.window[ReactSettings.productsDirectory] ||
  global.window[ReactSettings.productDetails] ||
  global.window[ReactSettings.exhibitorHub] ||
  global.window[ReactSettings.taskList] ||
  global.window[ReactSettings.ManageAdminRoles] ||
  global.window[ReactSettings.default] ||
  global.window[ReactSettings.mixpanel] ||
  global.window[ReactSettings.exhibitorHubOperationalTasks];

const getCurrentEnvironment = () => {
  const environment = global.window
    ? getReactSettings()?.environment
    : global.process.env.environment;
  if (!environment) return 'prod';
  return environment;
};
const setCdnUrl = () => {
  let environment = getCurrentEnvironment();
  if (!environment) {
    environment = 'prod';
  }
  window.CdnURL = cdnConfig[environment]?.cdnURL;
};
const isDevEnvironment = (environment) =>
  environment === 'localAgainstDevPkce' ||
  environment === 'localAgainstDev' ||
  environment === 'dev' ||
  environment === 'localAgainstShow';

const isShowEnvironment = (environment) => environment === 'show';

const getLocationOrigin = () => window.location.origin;

const getLocationQueryParam = () =>
  global.window && global.window.location.search;

const getQueryParamValue = (paramName) => {
  const queryString = window && window.location?.search;
  if (!queryString) return null;

  const urlParams = new URLSearchParams(queryString);
  return urlParams.get(paramName);
};

const appendQueryParam = (paramName, paramValue) => {
  const queryString = window && window.location?.search;

  const urlParams = new URLSearchParams(queryString);
  urlParams.set(paramName, paramValue);
  return urlParams.toString();
};

const removeQueryParam = (paramName) => {
  const queryString = window && window.location?.search;
  if (!queryString) return null;

  const urlParams = new URLSearchParams(queryString);
  urlParams.delete(paramName);
  return urlParams.toString();
};

const reloadLocation = (e) => {
  e.preventDefault();
  e.stopPropagation();
  window.location.reload(true);
};

const getLocationHref = () => window.location.href;

const normalizeAssetUrl = (url) => {
  const rawUrl = `${url ?? ''}`.trim();

  if (!rawUrl) {
    return rawUrl;
  }

  try {
    const parsedUrl = new URL(rawUrl);

    if (!['http:', 'https:'].includes(parsedUrl.protocol)) {
      return rawUrl;
    }

    const absoluteUrlMatch = rawUrl.match(/^(https?:\/\/)([^/?#]+)(.*)$/i);

    if (!absoluteUrlMatch) {
      return parsedUrl.toString();
    }

    const [, , , rawRest] = absoluteUrlMatch;
    const normalizedOrigin = `${parsedUrl.protocol}//${parsedUrl.host}`;

    return rawRest ? `${normalizedOrigin}${rawRest}` : normalizedOrigin;
  } catch {
    return rawUrl;
  }
};

const sanitizeAssetVariantToken = (value) =>
  `${value ?? ''}`.replace(/[^A-Za-z0-9_-]/g, '');

const sanitizeAssetFileName = (value) =>
  `${value ?? ''}`.replace(/[^A-Za-z0-9._-]/g, '');

const buildResizedAssetPath = (...tokens) =>
  tokens.map(sanitizeAssetVariantToken).filter(Boolean).join('-');

const getAssetUrlParts = (url) => {
  const normalizedUrl = normalizeAssetUrl(url);

  if (!normalizedUrl) {
    return null;
  }

  const [baseUrl, suffix = ''] = normalizedUrl.split(/([?#].*)/, 2);
  const pathSeparatorIndex = baseUrl.lastIndexOf('/');
  const basePath =
    pathSeparatorIndex >= 0 ? baseUrl.slice(0, pathSeparatorIndex + 1) : '';
  const rawFileName =
    pathSeparatorIndex >= 0 ? baseUrl.slice(pathSeparatorIndex + 1) : baseUrl;
  const extSeparatorIndex = rawFileName.lastIndexOf('.');
  const rawBaseName =
    extSeparatorIndex > 0
      ? rawFileName.slice(0, extSeparatorIndex)
      : rawFileName;
  const rawExtension =
    extSeparatorIndex > 0
      ? rawFileName.slice(extSeparatorIndex).toLowerCase()
      : '';
  const safeBaseName = sanitizeAssetFileName(rawBaseName);
  const safeExtension = sanitizeAssetFileName(rawExtension);

  if (!safeBaseName || rawFileName.includes('..')) {
    throw new Error('Invalid file name');
  }

  return {
    basePath,
    suffix,
    fileName:
      rawExtension === '.gif'
        ? `${safeBaseName}.png`
        : `${safeBaseName}${safeExtension}`,
  };
};

const buildResizedAssetUrl = (url, ...tokens) => {
  const assetUrlParts = getAssetUrlParts(url);

  if (!assetUrlParts) {
    return '';
  }

  const { basePath, suffix, fileName } = assetUrlParts;
  const resizedPath = buildResizedAssetPath(...tokens);

  return `${basePath}${resizedPath ? `${resizedPath}/` : ''}${fileName}${suffix}`;
};

const getResizedImage = (url, device, size) => {
  return buildResizedAssetUrl(url, device, size);
};

const getResizedProductImage = (url, device, size, displayPage) => {
  return buildResizedAssetUrl(url, device, size, displayPage);
};

const poll = (url) =>
  new Promise((resolve, reject) => {
    const firstRetryAfterMs = 200;
    const increaseWaitByFactor = 1.4;
    let times = 15;
    const p = (waitTimeInMs) =>
      fetch(url, { method: 'HEAD' }).then((r) => {
        times -= 1;
        if (r.ok) {
          resolve('ok');
        } else if (times === 0) {
          reject(new Error('timeout'));
        } else {
          setTimeout(p, waitTimeInMs, waitTimeInMs * increaseWaitByFactor);
        }
      });
    p(firstRetryAfterMs);
  });

const parseJwt = (token) => {
  const base64Url = token.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const jsonPayload = decodeURIComponent(
    atob(base64)
      .split('')
      .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
      .join(''),
  );

  return JSON.parse(jsonPayload);
};

const hasImageExtension = (fileName) => {
  const extension = fileName.toLowerCase().split('.').pop();
  return ImageFileExtensions.includes(extension);
};

const hasVideoExtension = (fileName) => {
  const format = fileName.toLowerCase().split('.').pop();
  return VideoFileExtensions.includes(`.${format}`);
};

const hasPdfExtension = (fileName) => {
  const extension = fileName.toLowerCase().split('.').pop();
  return DocumentFileExtensions.includes(extension);
};

const hasCertificateExtension = (fileName) => {
  const extension = fileName.toLowerCase().split('.').pop();
  return CertificateFileExtension === extension;
};

const formatBytes = (bytes, decimals = 0) => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  const convertedBytesInSize = (bytes / k ** i).toFixed(decimals);

  return `${convertedBytesInSize} ${sizes[i]}`;
};

const serviceResultHasError = (result) => {
  const { fieldErrors, errors, mustLogIn } = result;
  return (
    (fieldErrors != null && Object.keys(fieldErrors).length !== 0) ||
    errors?.length !== 0 ||
    mustLogIn
  );
};

const getTranslatedOrDefault = (translations, key) =>
  translations[key] ? translations[key] : key;

const constructErrorsFromServiceResult = (result, errorTranslations) => {
  if (result.mustLogIn) {
    return [];
  } else if (result.errors?.length > 0) {
    return result.errors;
  } else if (result.errorResponse?.length > 0) {
    return result.errorResponse.map((err) =>
      getTranslatedOrDefault(errorTranslations, err.extensions.code),
    );
  }

  return [];
};

const commaSeparateElements = (elements) =>
  elements.reduce((prev, curr) => [prev, ', ', curr]);

const findByLocale = (multilingual, locale) => {
  const lowerCaseLocale = locale.toLowerCase();
  return find(multilingual, (o) => o.locale.toLowerCase() === lowerCaseLocale);
};

const getLanguageCodeFromLocale = (locale) =>
  locale.split('-')[0].toLowerCase();

const getResourceIdFromLocation = (location, idPrefix) => {
  const regex = new RegExp('/('.concat(idPrefix).concat('-.*)/'));
  return regex.exec(location)[1];
};

const constructSSOUrl = (
  idpUrl,
  thirdPartyServiceId,
  eventEditionId,
  organisationGuid,
) =>
  `${idpUrl.endsWith('/') ? idpUrl : idpUrl.concat('/')}secure/SingleSignOn/initiate?organisationId=${organisationGuid}` +
  `&thirdPartyServicesId=${thirdPartyServiceId}&eventEditionId=${eventEditionId}`;

const constructLoginEmailUrl = (
  idpUrl,
  knownUrl,
  unknownUrl,
  eventEditionId,
  locale,
) =>
  `${idpUrl.endsWith('/') ? idpUrl : idpUrl.concat('/')}${RegRedirectConstants.LOGIN_EMAIL_PATH}?KnownUrl=${knownUrl}` +
  `&unknownUrl=${unknownUrl}&eventEditionId=${eventEditionId}&locale=${locale}`;

const didRequestFail = (result) =>
  result.errors.length > 0 ||
  Object.keys(result.fieldErrors).length > 0 ||
  result.mustLogIn ||
  false;

const getFromLocalStorage = (key) => ConsentStorage.getItem('localStorage', key);

const getShowPlanId = (eventEditionId) =>
  JSON.parse(getFromLocalStorage('showPlanningIds'))?.[
    eventEditionId
  ];

const setValueInLocalStorage = (key, value) =>
  ConsentStorage.setItem('localStorage', key, value);

const getFromSessionStorage = (key) =>
  JSON.parse(ConsentStorage.getItem('sessionStorage', key));

const hasConsent = (groupId, onChange) => {
  ConsentStorage.initialize();

  const isGranted = () => {
    const activeGroups = global.window?.OnetrustActiveGroups;
    return (
      typeof activeGroups === 'string' && activeGroups.includes(`,${groupId},`)
    );
  };

  if (typeof onChange === 'function') {
    onChange(isGranted());
    const oneTrust = global.window?.OneTrust;
    if (oneTrust && typeof oneTrust.OnConsentChanged === 'function') {
      oneTrust.OnConsentChanged(() => onChange(isGranted()));
    }
  }

  return isGranted();
};
const buildHeaders = (customHeaders) =>
  authorizationStore.getToken().then((token) => {
    const clientId = authorizationStore.getClientId();
    const bearer = token ? { Authorization: `Bearer ${token}` } : {};
    const xClientId = clientId ? { 'X-ClientId': clientId } : {};
    const xCorrelationId = { 'X-CorrelationId': uuidv4() };
    return Object.assign(
      { Accept: 'application/json' },
      bearer,
      xClientId,
      xCorrelationId,
      { ...customHeaders },
    );
  });

const getPackageName = (packageId) => {
  if (packageId === null || packageId === undefined) {
    return '';
  }
  return Object.keys(Packages).find((key) => Packages[key] === packageId);
};

const constructQueryParamWithExistingQueryParams = (
  paramName,
  paramValue,
  currentUrlQueryParamString,
) => {
  return Object.assign(
    {},
    omit(qs.parse(currentUrlQueryParamString), [paramName]),
    {
      [paramName]: paramValue,
    },
  );
};

const isMSH = () => {
  const authToken = authorizationStore.getToken();
  return authToken.then((token) =>
    parseJwt(token).scope.includes('urn:rx:digital:sharer:write'),
  );
};

const isSharerSelf = (organisationGuid) => {
  const authToken = authorizationStore.getToken();
  return authToken.then((token) =>
    parseJwt(token)?.rxOrg?.includes(organisationGuid),
  );
};

const getUserId = () => {
  const authToken = authorizationStore.getToken();
  return authToken.then((token) => parseJwt(token).sub);
};

const isDiscoverEnabled = (eventEditionId) => {
  return !!getDiscoverEventConfig(eventEditionId);
};

const getContactFormCode = (eventEditionId, locale) => {
  const requestQuoteConfig = getRequestQuoteConfig(eventEditionId);
  if (requestQuoteConfig == null) {
    return null;
  }
  const formCodeConfig = requestQuoteConfig.formCode;
  return formCodeConfig[locale] === undefined
    ? formCodeConfig.default
    : formCodeConfig[locale];
};

const getTertiaryColor = () => {
  const fallBackColor = '#075065';
  const settings =
    global.window?.reactSettings || global.window?.reactSettingsProductDetails;
  if (settings?.props?.themes?.brandTertiary) {
    return settings.props.themes.brandTertiary;
  }
  return fallBackColor;
};

const getDiscoverConfig = () => {
  let environment = getCurrentEnvironment();
  return DiscoverConfiguration[environment];
};

const getDiscoverEventConfig = (eventEditionId) => {
  let environment = getCurrentEnvironment();
  return DiscoverConfiguration[environment].events.find(
    (e) => e.eventEditionId === eventEditionId,
  );
};

const groupBy = function (arr, key, value) {
  return arr.reduce(function (rv, x) {
    rv[x[key]] = rv[x[key]] || [];
    rv[x[key]].push(x[value]);
    return rv;
  }, {});
};

const haveValidCategory = (products, eventEditionId) => {
  const discoverEventConfig = getDiscoverEventConfig(eventEditionId);
  if (!discoverEventConfig.atlasIngredientsCategoryId) {
    return true;
  }
  return !!products.find(
    (p) =>
      !!p.filterAttributes &&
      !!p.filterAttributes.find(
        (f) =>
          !!f.responses.find(
            (r) =>
              r.parentId === discoverEventConfig.atlasIngredientsCategoryId,
          ),
      ),
  );
};

const capitalizeFirstLetter = (string) => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};

const getSEOFriendlyName = (name) => {
  if (!name) return '';
  const regex = /[^\w\s\d\p{L}-]/gu;
  const seoFriendlyName = name.replace(regex, '');
  return encodeURIComponent(seoFriendlyName.toLowerCase());
};

const isEventEditionIdExcludedForSurvey = (eventEditionId) => {
  const environment = getCurrentEnvironment();
  const excludedIds =
    SurveyConfiguration[environment][0]['eventEditionIdsToExclude'];
  return excludedIds.includes(eventEditionId);
};

const getVerintSurveyUri = (surveyUriKey) => {
  const environment = getCurrentEnvironment();
  const surveyUri = SurveyConfiguration[environment][0][surveyUriKey];
  return surveyUri != null ? surveyUri : null;
};

const saveToSessionStorage = (key, value) =>
  ConsentStorage.setItem('sessionStorage', key, JSON.stringify(value));

const isEmptyOrWhitespaces = (str) => str == null || str.trim() === '';
const isValidRxEventCode = (str) =>
  str == null || (isFinite(str) && str.length === 5);

const getDocumentThumbnail = (url) => {
  const filename = url.split('/').pop();
  const filenameWithoutExtension = filename.substring(
    0,
    filename.lastIndexOf('.'),
  );
  const formattedUrl = url.substring(0, url.lastIndexOf('/'));
  return `${formattedUrl}/thumbnail/${filenameWithoutExtension}.jpg`;
};

const removePdfFileNameExtension = (fileName) => {
  if (fileName && fileName.toLowerCase().endsWith('.pdf')) {
    return fileName.slice(0, -4);
  }
  return fileName;
};

const getNoOfItemsToShowInGallery = (type) => {
  if (type === ItemType.Product)
    return window.matchMedia('(max-width: 767px)').matches
      ? GalleryCollapsedViewMaxItems.product.mobile
      : GalleryCollapsedViewMaxItems.product.desktopOrTablet;
  if (type === ItemType.Document)
    return window.matchMedia('(max-width: 767px)').matches
      ? GalleryCollapsedViewMaxItems.document.mobile
      : GalleryCollapsedViewMaxItems.document.desktopOrTablet;
  else return 0;
};

const sortItems = (type, items) => {
  if (type === ItemType.Product)
    return [...items].sort(
      (item1, item2) =>
        new Date(item2.lastUpdatedAt) - new Date(item1.lastUpdatedAt),
    );
  if (type === ItemType.Document)
    return [...items].sort(
      (item1, item2) => new Date(item2.createdAt) - new Date(item1.createdAt),
    );
  else return items;
};

const getRecommendationURL = (interfaceLocale, exhibitorId) => {
  const domain = getLocationOrigin();
  const locale = interfaceLocale.toLowerCase();
  return `${domain}/east/${locale}/${EXHIBITOR_DIRECTORY_RECOMMENDATION.recommendationPath}?r=${EXHIBITOR_DIRECTORY_RECOMMENDATION.exhibitorDirectory}&e=${exhibitorId}`;
};

const getVideoDuration = async (file) => {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'metadata';

    video.onloadedmetadata = function () {
      window.URL.revokeObjectURL(video.src);
      resolve(video.duration);
    };

    video.onerror = function () {
      reject(new Error('Failed to load video metadata'));
    };

    video.src = URL.createObjectURL(file);
  });
};

const getProductVideoOrImageUrl = (product) => {
  if (product?.video?.customThumbnailUrl) {
    return product.video.customThumbnailUrl;
  } else if (product?.video?.defaultThumbnailUrl) {
    return product.video.defaultThumbnailUrl;
  } else if (product?.imageUrl) {
    return product?.imageUrl;
  } else {
    return product?.thumbnailUrl;
  }
};

const getDashboardURL = (
  reactSettingsLink,
  eventEditionId,
  exhibitingOrganisationId,
) => {
  if (reactSettingsLink) {
    if (reactSettingsLink.includes('{0}'))
      return StringFormatter.format(reactSettingsLink, [
        `${eventEditionId}.${exhibitingOrganisationId}`,
      ]);
    return reactSettingsLink.replace(
      '.html',
      `.${eventEditionId}.${exhibitingOrganisationId}.html`,
    );
  }
  return `/exhibitor-insights/${eventEditionId}/${exhibitingOrganisationId}`;
};

// https://www.seanmcp.com/articles/replace-math-random-with-crypto-in-javascript/
const generateRandomNumber = () => {
  const crypto = window.crypto || window.msCrypto;
  return crypto.getRandomValues(new Uint32Array(1))[0] / Math.pow(2, 32);
};

const isStrictEmailAddress = (email) => {
  // Forbidden characters scan
  const forbidden = /[<>()[\]{}\\'",;:|!#$%^&*=?`~]/;
  if (forbidden.test(email)) return false;

  // Structural regex: local-part + '@' + domain + '.' + TLD (2–63 letters)
  const structural = /^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,63}$/i;
  if (!structural.test(email)) return false;

  // Enforce .com domain (case-insensitive)
  return email.toLowerCase().endsWith('.com');
};

const getUtf8ByteCount = (filename) => {
  if (typeof filename !== 'string') {
    throw new TypeError('The input must be a string');
  }
  const encoder = new TextEncoder();
  const byteArray = encoder.encode(filename);
  return byteArray.length;
};

// Mercury-owned pages are served outside AEM (via Cloudflare), so they need a real path, not a #/ hash route.
const getMercuryExhibitorHubUrl = (path, hubUrl) => {
  const { origin, pathname } = new URL(hubUrl || window.location.href, window.location.origin);
  const hubPath = pathname
    .replace(/\/exhibitor-hub(\.[^/]*)?\.html.*$/, '/exhibitor-hub.html')
    .replace(/\/$/, '');
  return `${origin}${hubPath}${path}`;
};

const MERCURY_TO_LEGACY_HUB_ROUTES = {
  'invite-manager': 'invite-manager',
  'product-list': 'productList',
  badges: 'badges',
};

// Shows without the Cloudflare rule get the Mercury URL served by AEM; map it back to the legacy #/ route.
const getLegacyExhibitorHubHashUrl = ({ pathname, search }) => {
  const match = pathname.match(
    /^(.*\/exhibitor-hub(?:\.[^/]*)?\.html)\/(invite-manager|product-list|badges)\/([^/]+)\/([^/]+)\/?$/,
  );
  if (!match) {
    return null;
  }
  const [, hubPath, route, eventEditionId, exhibitingOrganisationId] = match;
  return `${hubPath}#/${MERCURY_TO_LEGACY_HUB_ROUTES[route]}/${eventEditionId}/${exhibitingOrganisationId}${search}`;
};

// Keep the full path prefix before "/exhibitor-hub" (e.g. "/autumn/ja-jp"), not just the first segment.
const updateExhibitorHubUrl = (eventEditionId, exhibitingOrganisationId) => {
  const { pathname } = window.location;
  const hubIndex = pathname.indexOf('/exhibitor-hub');
  const basePath = hubIndex >= 0 ? pathname.substring(0, hubIndex) : `/${pathname.split('/')[1]}`;
  const url = `${basePath}/exhibitor-hub.${eventEditionId}.${exhibitingOrganisationId}.html#/`;

  window.history.replaceState(window.history.state, '', url);
};

module.exports = {
  hasImageExtension,
  hasPdfExtension,
  hasCertificateExtension,
  poll,
  getResizedImage,
  getResizedProductImage,
  clickLink,
  filterFieldErrors,
  setLocationHref,
  getCurrentEnvironment,
  setCdnUrl,
  isDevEnvironment,
  getLocationOrigin,
  getLocationHref,
  generateRandomNumber,
  reloadLocation,
  formatBytes,
  isRxAdmin,
  serviceResultHasError,
  getTranslatedOrDefault,
  constructErrorsFromServiceResult,
  commaSeparateElements,
  findByLocale,
  getLanguageCodeFromLocale,
  getFromLocalStorage,
  setValueInLocalStorage,
  getLocationQueryParam,
  getResourceIdFromLocation,
  isParticipantBase,
  constructErrorFieldMessageMapFromGraphQlResponse,
  didRequestFail,
  captureResponseCodeAsError,
  constructSSOUrl,
  constructLoginEmailUrl,
  buildHeaders,
  getPackageName,
  constructQueryParamWithExistingQueryParams,
  parseJwt,
  isMSH,
  isSharerSelf,
  isShowEnvironment,
  getUserId,
  getFromSessionStorage,
  saveToSessionStorage,
  hasConsent,
  getShowPlanId,
  getReactSettings,
  getSEOFriendlyName,
  isDiscoverEnabled,
  getDiscoverConfig,
  getDiscoverEventConfig,
  groupBy,
  haveValidCategory,
  capitalizeFirstLetter,
  getTertiaryColor,
  isEmptyOrWhitespaces,
  isValidRxEventCode,
  getVerintSurveyUri,
  getDocumentThumbnail,
  removePdfFileNameExtension,
  getNoOfItemsToShowInGallery,
  sortItems,
  isEventEditionIdExcludedForSurvey,
  getRecommendationURL,
  getQueryParamValue,
  appendQueryParam,
  removeQueryParam,
  getVideoDuration,
  hasVideoExtension,
  getProductVideoOrImageUrl,
  getDashboardURL,
  isStrictEmailAddress,
  getUtf8ByteCount,
  getMercuryExhibitorHubUrl,
  getLegacyExhibitorHubHashUrl,
  updateExhibitorHubUrl,
};
